/**
 * PlayjectorService - Production implementation using Electron IPC
 *
 * Wraps the actual Playjector IPC communication for production use.
 * Only available when running in Electron (not in browser).
 *
 * Scope: PlayjectorCoordinator only
 */

import type {
  IPlayjectorService,
  PlayjectorState,
  CampaignLaunchResult,
  MacroStepCaptureSpec,
  MacroStepCaptureResult,
  MacroStepReply,
  ScheduledScreenshotSpec,
  ScheduledScreenshotResult,
  RecordingStopResult,
  SessionScreenshotResult,
} from './interfaces/IPlayjectorService';
import { ipcSend } from '../../api/messaging/protocols/ipc';
import {
  generators,
  macroStepErrorCodeFromLegacyProse,
  PlaycastMessage,
  type MacroStepErrorCode,
  type PlaycastMessageProcessMacroStep,
} from '@playcastdotio/Messaging';
import { playjectorHostModeState } from '../../api/stores/shared';
import { PlaycastHostEdition } from '@playcastdotio/CoreTypes';
import * as Sentry from '@sentry/react';
import { createMacroStepError } from '../../coordinators/macro/helpers';

/**
 * How long to wait for a `processMacroStepReplyMSG` from Playjector before
 * rejecting the in-flight `requestMacroStep` promise. Sized generously — the
 * C++ side serializes the screenshot capture, hash compute, and JSON reply,
 * but typical replies arrive in well under 2s.
 */
const MACRO_STEP_REPLY_TIMEOUT_MS = 10_000;

/** After walk teardown, keep the D3 native latch until a late reply or this drain. */
const MACRO_STEP_NATIVE_SLOT_DRAIN_MS = 30_000;

/**
 * Timeout for recording control IPC round-trips. Recording start/stop
 * involves encoder initialization; allow generous time. Must exceed the C++
 * side's own worst case (StopBackendAndWaitForOutput polls up to 15s for MP4
 * finalization) plus transit, so a legitimate max-length stop doesn't race.
 */
const RECORDING_CONTROL_TIMEOUT_MS = 20_000;

/**
 * Timeout for the recording-state status-query round-trip. Native answers
 * synchronously from RecordControlManager getters; short headroom suffices.
 */
const RECORD_STATUS_QUERY_TIMEOUT_MS = 5_000;

/**
 * Timeout for session screenshot capture IPC round-trips. Must exceed the C++
 * side's own 10s capture wait (DisplayManager::RequestScreenshot) plus transit,
 * so a slow-but-successful capture isn't cut off by the host.
 */
const SESSION_SCREENSHOT_TIMEOUT_MS = 15_000;

/**
 * How long to wait for the encoder's `encoderStatusReplyMSG` "STARTED" signal
 * after a recording start has been acknowledged. The signal is emitted from the
 * native recording thread once frames are actually flowing; typical latency is
 * well under a second, so 10s is generous.
 */
const ENCODER_READY_TIMEOUT_MS = 10_000;

/**
 * Variant key used when requestScheduledScreenshot wraps a macro step request.
 * The watcher only needs a single capture, so we always use this key.
 */
const SCHEDULED_VARIANT_KEY = 'scheduled';

interface MacroStepWaiter {
  resolve(value: Record<string, MacroStepCaptureResult>): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface RecordingControlWaiter {
  resolve(value: { status: string; filePath?: string }): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
  takeId: string;
  sentAt: number;
}

interface SessionScreenshotWaiter {
  resolve(value: SessionScreenshotResult): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
  sessionName: string;
  sentAt: number;
}

interface EncoderReadyWaiter {
  resolve(): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
  takeId: string;
  startedWaitingAt: number;
}

interface RecordStatusQueryWaiter {
  resolve(value: { isRecording: boolean; activeTakeId: string }): void;
  reject(reason: Error): void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export class PlayjectorService implements IPlayjectorService {
  private campaignLaunchListeners: Array<(result: CampaignLaunchResult) => void> = [];
  private pendingMacroStep: MacroStepWaiter | null = null;
  /**
   * True from `ipcSend(processMacroStep)` until a reply arrives — including
   * after the JS waiter times out. C++ may still be hashing; starting another
   * capture would overwrite `macroVariants` (Wait D3: no overlapping captures).
   * A late reply with no waiter is D8 ignore and clears this flag.
   */
  private nativeMacroStepOutstanding = false;
  private nativeSlotDrainTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingRecordingControl: RecordingControlWaiter | null = null;
  private pendingSessionScreenshot: SessionScreenshotWaiter | null = null;
  private pendingEncoderReady: EncoderReadyWaiter | null = null;
  private pendingRecordStatusQuery: RecordStatusQueryWaiter | null = null;

  constructor() {
    console.log('[PlayjectorService] 🚀 Playjector service initialized (Electron mode)');
  }

  isAvailable(): boolean {
    return typeof window !== 'undefined' && window.playcast?.isHost === true;
  }

  sendHostModeChanged(
    edition: PlaycastHostEdition,
    matchmakeable: boolean,
    steamGuestLogin: boolean,
    guestAccount: boolean,
    credentials: string,
    hostAuthId: string,
    campaignId?: string,
  ): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Sending hostModeChanged via IPC');
    // Note: campaignId is not used by real IPC (Playjector gets campaign from Electron backend)

    ipcSend(
      generators.host.hostModeChanged(
        edition,
        matchmakeable,
        steamGuestLogin,
        guestAccount,
        credentials,
        hostAuthId,
      ),
    );
  }

  updateInvitation(invitationId: string, gameName: string, steamId: string): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Updating invitation via realtime');

    // TODO: Import and use realtime sendMessage
    // sendMessage(
    //   websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_UPDATE_INVITATION, {
    //     invitationId,
    //     name: gameName,
    //     steamId,
    //   }),
    // );
  }

  killPlayjector(): void {
    if (!this.isAvailable() || !window.playcast) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Killing Playjector');
    window.playcast.killPlayjector();
  }

  terminateGame(hostAuthId: string): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Terminating game process (pavilion)');
    ipcSend(generators.process.processTerminate(hostAuthId, 'pavilion'));
  }

  connectPlayjector(): void {
    if (!this.isAvailable() || !window.playcast) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] Connecting to Playjector');
    window.playcast.connectPlayjector();
  }

  onStateChange(callback: (state: PlayjectorState) => void): () => void {
    // Subscribe to the shared playjectorHostModeState store
    const listenerId = playjectorHostModeState.addListener((state) => {
      callback(state as PlayjectorState);
    });

    // Return unsubscribe function
    return () => {
      playjectorHostModeState.removeListener(listenerId);
    };
  }

  getCurrentState(): PlayjectorState {
    return playjectorHostModeState.currentState as PlayjectorState;
  }

  onCampaignLaunchResult(callback: (result: CampaignLaunchResult) => void): () => void {
    // Add listener
    this.campaignLaunchListeners.push(callback);

    // TODO: Hook into IPC pavilionLaunchReply messages
    // The IPC handler (apps/host/host-frontend/src/api/messaging/handlers/ipc.ts)
    // currently sends these directly to campaignSwitchTracker.
    // In the new architecture, we should route through this service.
    console.warn('[PlayjectorService] onCampaignLaunchResult: IPC integration not yet implemented');

    // Return unsubscribe function
    return () => {
      const index = this.campaignLaunchListeners.indexOf(callback);
      if (index > -1) {
        this.campaignLaunchListeners.splice(index, 1);
      }
    };
  }

  launchCampaign(hostAuthId: string, edition: PlaycastHostEdition, launchPackage: any): void {
    if (!this.isAvailable()) {
      console.warn('[PlayjectorService] Playjector not available (not in Electron)');
      return;
    }

    console.log('[PlayjectorService] 🎮 Launching campaign via processLaunch IPC:', {
      edition,
      launcher: launchPackage.launcher,
      hostAuthId,
    });

    Sentry.logger.info(`[PlayjectorService] 🎮 Launching campaign via processLaunch IPC: ${edition}, ${launchPackage.launcher}`);

    ipcSend(
      generators.process.processLaunch(
        hostAuthId,
        edition,
        launchPackage,
      ),
    );
  }

  /**
   * Internal: Notify campaign launch listeners
   * This will be called by IPC handler when pavilionLaunchReply is received
   */
  public notifyCampaignLaunchResult(result: CampaignLaunchResult): void {
    console.log('[PlayjectorService] 📨 Notifying listeners of campaign launch result:', result);
    this.campaignLaunchListeners.forEach((listener) => {
      try {
        listener(result);
      } catch (error) {
        console.error('[PlayjectorService] Campaign launch listener error:', error);
      }
    });
  }

  // ========================================
  // Stateful macro step IPC adapter
  // ========================================

  public async requestMacroStep(
    captures: Record<string, MacroStepCaptureSpec>,
    screenshotName?: string,
  ): Promise<Record<string, MacroStepCaptureResult>> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }

    if (this.pendingMacroStep || this.nativeMacroStepOutstanding) {
      // Playjector tracks one in-flight macro step at a time (see
      // ProcessDirectory::onMacroStep storing into m_ProcessInfo.macroVariants).
      // Two concurrent requests would have their replies aliased; reject early.
      // `nativeMacroStepOutstanding` stays set after a JS timeout until the
      // late C++ reply (D8) so Wait cannot overlap captures (D3).
      throw createMacroStepError('A macro step request is already in flight', 'request_in_flight');
    }

    if (Object.keys(captures).length === 0) {
      throw new Error('requestMacroStep called with empty captures map');
    }

    const variants: Record<string, MacroStepCaptureSpec> = {};
    for (const [captureId, spec] of Object.entries(captures)) {
      variants[captureId] = spec.region
        ? { width: spec.width, height: spec.height, region: spec.region }
        : { width: spec.width, height: spec.height };
    }

    const message = buildProcessMacroStepMessage(variants, screenshotName);

    const promise = new Promise<Record<string, MacroStepCaptureResult>>((resolve, reject) => {
      const waiter: MacroStepWaiter = {
        resolve,
        reject,
        timeoutId: setTimeout(() => {
          console.error(
            `[StatefulMacroSystemStep] ⏱️ processMacroStep reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms — Playjector did not reply`,
            {
              timeoutMs: MACRO_STEP_REPLY_TIMEOUT_MS,
              captureIds: Object.keys(variants),
              captureCount: Object.keys(variants).length,
            },
          );
          // A capture that is never answered usually means the game died
          // mid-walk. Without this the walk just goes quiet for 30s and then
          // reports success via the legacy fallback.
          Sentry.logger.error(
            `[StatefulMacroSystemStep] processMacroStep reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms `
              + `— Playjector did not reply: captureIds=[${Object.keys(variants).join(',')}]`,
          );
          if (this.pendingMacroStep === waiter) {
            this.pendingMacroStep = null;
          }
          // Keep nativeMacroStepOutstanding until the late reply (D8).
          reject(createMacroStepError(
            `Macro step reply timed out after ${MACRO_STEP_REPLY_TIMEOUT_MS}ms`,
            'reply_timeout',
          ));
        }, MACRO_STEP_REPLY_TIMEOUT_MS),
      };
      this.pendingMacroStep = waiter;
      this.nativeMacroStepOutstanding = true;
    });

    const requestSummary = {
      variantCount: Object.keys(variants).length,
      captureIds: Object.keys(variants),
      variants: Object.fromEntries(
        Object.entries(variants).map(([id, spec]) => [
          id,
          {
            width: spec.width,
            height: spec.height,
            region: spec.region ?? null,
          },
        ]),
      ),
      timeoutMs: MACRO_STEP_REPLY_TIMEOUT_MS,
    };
    console.log(
      '[StatefulMacroSystemStep] 📤 Asking Playjector for screenshot(s) (processMacroStepMSG)',
      requestSummary,
    );
    Sentry.logger.info(
      '[StatefulMacroSystemStep] Playjector macro-step region request',
      requestSummary,
    );
    ipcSend(message);

    return promise;
  }

  public notifyMacroStepResult(reply: MacroStepReply & { error?: string; errorCode?: string }): void {
    this.clearNativeSlotDrain();
    this.nativeMacroStepOutstanding = false;
    const waiter = this.pendingMacroStep;
    if (!waiter) {
      console.warn('[StatefulMacroSystemStep] ⚠️  Received Playjector macro step reply with no waiter — dropping');
      return;
    }
    this.pendingMacroStep = null;
    clearTimeout(waiter.timeoutId);

    if (reply.error) {
      console.error('[StatefulMacroSystemStep] ❌ Playjector macro step reply error:', reply.error);
      const nativeError = reply.error;
      const wireCode = reply.errorCode as MacroStepErrorCode | undefined;
      const code = wireCode ?? macroStepErrorCodeFromLegacyProse(nativeError);
      if (code) {
        waiter.reject(createMacroStepError(`Playjector macro step error: ${nativeError}`, code, {
          codeInferred: !wireCode,
          nativeError,
        }));
      } else {
        const err = new Error(`Playjector macro step error: ${nativeError}`) as import('../../coordinators/macro/helpers').MacroStepError;
        Object.defineProperty(err, 'nativeError', { value: nativeError, enumerable: true });
        waiter.reject(err);
      }
      return;
    }

    if (!reply.variantResults || typeof reply.variantResults !== 'object') {
      console.error('[StatefulMacroSystemStep] ❌ Playjector macro step reply missing variantResults — rejecting', {
        replyShape: Object.keys(reply ?? {}),
      });
      waiter.reject(new Error('Macro step reply missing variantResults'));
      return;
    }

    const variantCount = Object.keys(reply.variantResults).length;
    console.log(
      '[StatefulMacroSystemStep] 📨 Received Playjector screenshot reply',
      {
        variantCount,
        captureIds: Object.keys(reply.variantResults),
        variantResults: Object.fromEntries(
          Object.entries(reply.variantResults).map(([id, r]) => [
            id,
            { width: r.width, height: r.height, hash: r.hash },
          ]),
        ),
      },
    );
    waiter.resolve(reply.variantResults);
  }

  public abandonMacroStep(): void {
    const waiter = this.pendingMacroStep;
    if (waiter) {
      this.pendingMacroStep = null;
      clearTimeout(waiter.timeoutId);
      waiter.reject(createMacroStepError('Macro step abandoned', 'abandoned'));
    }
    // Keep nativeMacroStepOutstanding until D8 or drain so a new
    // requestMacroStep cannot race a late C++ reply onto a fresh waiter.
    if (this.nativeMacroStepOutstanding) {
      this.armNativeSlotDrain();
    }
  }

  private armNativeSlotDrain(): void {
    if (this.nativeSlotDrainTimer) {
      return;
    }
    this.nativeSlotDrainTimer = setTimeout(() => {
      this.nativeSlotDrainTimer = null;
      this.nativeMacroStepOutstanding = false;
    }, MACRO_STEP_NATIVE_SLOT_DRAIN_MS);
  }

  private clearNativeSlotDrain(): void {
    if (!this.nativeSlotDrainTimer) {
      return;
    }
    clearTimeout(this.nativeSlotDrainTimer);
    this.nativeSlotDrainTimer = null;
  }

  // ========================================
  // Scheduled screenshot (delegates to macro step)
  // ========================================

  public async requestScheduledScreenshot(
    spec: ScheduledScreenshotSpec,
  ): Promise<ScheduledScreenshotResult> {
    const captures: Record<string, MacroStepCaptureSpec> = {
      [SCHEDULED_VARIANT_KEY]: { width: spec.width, height: spec.height },
    };

    const results = await this.requestMacroStep(captures, 'scheduled_latest');
    const result = results[SCHEDULED_VARIANT_KEY];

    if (!result) {
      throw new Error('Scheduled screenshot: no result returned for variant');
    }

    return { hash: result.hash, width: result.width, height: result.height };
  }

  public notifyScheduledScreenshotResult(
    _reply: ScheduledScreenshotResult & { error?: string },
  ): void {
    // No-op: scheduled screenshots now route through the macro step reply path.
    // Kept for interface compatibility with MockPlayjectorService.
    console.warn('[ScheduledScreenshot] notifyScheduledScreenshotResult is deprecated — replies route via macroStep');
  }

  // ========================================
  // Recording control IPC adapter
  // ========================================

  public async startRecording(takeId: string, cameraName?: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingRecordingControl) {
      throw new Error('A recording control request is already in flight');
    }

    const message = generators.recordControl.startRecording('host', takeId, cameraName);
    const sendTs = Date.now();
    const promise = new Promise<{ status: string; filePath?: string }>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRecordingControl = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder startRecording timed out', {
          feature: 'macro-recorder', takeId, timeoutMs: RECORDING_CONTROL_TIMEOUT_MS,
        });
        reject(new Error(`startRecording timed out after ${RECORDING_CONTROL_TIMEOUT_MS}ms`));
      }, RECORDING_CONTROL_TIMEOUT_MS);
      this.pendingRecordingControl = { resolve, reject, timeoutId, takeId, sentAt: sendTs };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send startRecording', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'startRecording', takeId, cameraName: cameraName ?? 'default',
    });
    console.log('[PlayjectorService] 📤 Sending startRecording IPC', { takeId });
    ipcSend(message);

    const result = await promise;
    if (result.status !== 'started') {
      throw new Error(`startRecording unexpected status: ${result.status}`);
    }
  }

  public async stopRecording(takeId: string): Promise<RecordingStopResult> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingRecordingControl) {
      throw new Error('A recording control request is already in flight');
    }

    const message = generators.recordControl.stopRecording('host', takeId);
    const sendTs = Date.now();
    const promise = new Promise<{ status: string; filePath?: string }>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRecordingControl = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder stopRecording timed out', {
          feature: 'macro-recorder', takeId, timeoutMs: RECORDING_CONTROL_TIMEOUT_MS,
        });
        reject(new Error(`stopRecording timed out after ${RECORDING_CONTROL_TIMEOUT_MS}ms`));
      }, RECORDING_CONTROL_TIMEOUT_MS);
      this.pendingRecordingControl = { resolve, reject, timeoutId, takeId, sentAt: sendTs };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send stopRecording', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'stopRecording', takeId,
    });
    console.log('[PlayjectorService] 📤 Sending stopRecording IPC', { takeId });
    ipcSend(message);

    const result = await promise;
    if (result.status !== 'finished') {
      throw new Error(`stopRecording unexpected status: ${result.status}`);
    }
    if (!result.filePath) {
      throw new Error('stopRecording reply missing filePath');
    }
    return { filePath: result.filePath };
  }

  public notifyRecordControlStatus(reply: {
    status: string;
    takeId: string;
    filePath?: string;
    error?: string;
  }): void {
    const waiter = this.pendingRecordingControl;
    if (!waiter) {
      Sentry.logger.info('[PlayjectorService] macro-recorder recordControlStatus reply with no waiter', {
        feature: 'macro-recorder', takeId: reply.takeId, status: reply.status,
      });
      console.warn('[PlayjectorService] recordControlStatus reply with no waiter — dropping');
      return;
    }
    if (waiter.takeId !== reply.takeId) {
      Sentry.logger.info('[PlayjectorService] macro-recorder recordControlStatus takeId mismatch', {
        feature: 'macro-recorder', expected: waiter.takeId, received: reply.takeId,
      });
      console.warn('[PlayjectorService] recordControlStatus takeId mismatch — dropping', {
        expected: waiter.takeId,
        received: reply.takeId,
      });
      return;
    }
    this.pendingRecordingControl = null;
    clearTimeout(waiter.timeoutId);

    const latencyMs = Date.now() - waiter.sentAt;
    Sentry.logger.info('[PlayjectorService] macro-recorder notifyRecordControlStatus resolved', {
      feature: 'macro-recorder', takeId: reply.takeId, status: reply.status, latencyMs,
    });

    if (reply.error) {
      waiter.reject(new Error(`Recording control error: ${reply.error}`));
      return;
    }
    waiter.resolve({ status: reply.status, filePath: reply.filePath });
  }

  public async queryRecordingState(): Promise<{ isRecording: boolean; activeTakeId: string }> {
    if (!this.isAvailable()) {
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingRecordStatusQuery) {
      throw new Error('A recording state query is already in flight');
    }

    const message = generators.recordControl.statusQuery('host');

    const promise = new Promise<{ isRecording: boolean; activeTakeId: string }>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRecordStatusQuery = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder statusQuery timed out', {
          feature: 'macro-recorder', timeoutMs: RECORD_STATUS_QUERY_TIMEOUT_MS,
        });
        reject(new Error(`statusQuery timed out after ${RECORD_STATUS_QUERY_TIMEOUT_MS}ms`));
      }, RECORD_STATUS_QUERY_TIMEOUT_MS);
      this.pendingRecordStatusQuery = { resolve, reject, timeoutId };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send statusQuery', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'statusQuery',
    });
    ipcSend(message);

    return promise;
  }

  public notifyRecordStatusQuery(reply: { isRecording?: boolean; activeTakeId?: string }): void {
    const waiter = this.pendingRecordStatusQuery;
    if (!waiter) {
      console.warn('[PlayjectorService] recordStatusQuery reply with no waiter — dropping');
      Sentry.logger.info('[PlayjectorService] macro-recorder recordStatusQuery dropped — no waiter', {
        feature: 'macro-recorder',
      });
      return;
    }
    this.pendingRecordStatusQuery = null;
    clearTimeout(waiter.timeoutId);

    const isRecording = reply.isRecording === true;
    const activeTakeId = typeof reply.activeTakeId === 'string' ? reply.activeTakeId : '';
    Sentry.logger.info('[PlayjectorService] macro-recorder recordStatusQuery resolved', {
      feature: 'macro-recorder', isRecording, activeTakeId,
    });
    waiter.resolve({ isRecording, activeTakeId });
  }

  // ========================================
  // Encoder readiness (encoderStatusReplyMSG)
  // ========================================

  public waitForEncoderReady(takeId: string): Promise<void> {
    if (!this.isAvailable()) {
      return Promise.reject(new Error('Playjector not available (not in Electron)'));
    }
    if (this.pendingEncoderReady) {
      return Promise.reject(new Error('An encoder-ready wait is already in flight'));
    }

    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingEncoderReady = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder waitForEncoderReady timed out', {
          feature: 'macro-recorder', takeId, timeoutMs: ENCODER_READY_TIMEOUT_MS,
        });
        reject(new Error(`waitForEncoderReady timed out after ${ENCODER_READY_TIMEOUT_MS}ms`));
      }, ENCODER_READY_TIMEOUT_MS);
      this.pendingEncoderReady = { resolve, reject, timeoutId, takeId, startedWaitingAt: Date.now() };
    });
  }

  public notifyEncoderStatus(reply: { cameraName?: string; result: string }): void {
    const waiter = this.pendingEncoderReady;
    if (!waiter) {
      // No waiter: the legacy game-clip path also emits encoder status updates,
      // so an unsolicited status is normal — stay silent.
      return;
    }
    // Native sends uppercase ("STARTED"/"FINISHED"); normalize defensively.
    if (reply.result?.toLowerCase() !== 'started') {
      return;
    }
    this.pendingEncoderReady = null;
    clearTimeout(waiter.timeoutId);

    const latencyMs = Date.now() - waiter.startedWaitingAt;
    Sentry.logger.info('[PlayjectorService] macro-recorder encoder STARTED — waiter resolved', {
      feature: 'macro-recorder', takeId: waiter.takeId, cameraName: reply.cameraName, latencyMs,
    });
    waiter.resolve();
  }

  // ========================================
  // Session screenshot IPC adapter
  // ========================================

  public async captureSessionScreenshot(
    sessionName: string,
    width: number,
    height: number,
  ): Promise<SessionScreenshotResult> {
    if (!this.isAvailable()) {
      Sentry.captureMessage('[MACRO-SCREENSHOT] captureSessionScreenshot rejected — Playjector not available', {
        level: 'error',
        extra: { sessionName },
      });
      throw new Error('Playjector not available (not in Electron)');
    }
    if (this.pendingSessionScreenshot) {
      // The single in-flight slot is occupied. Capture what is holding it vs the
      // request we just rejected — this is the top signal for "in flight forever".
      Sentry.captureMessage('[MACRO-SCREENSHOT] captureSessionScreenshot rejected — already in flight', {
        level: 'warning',
        extra: {
          rejectedSessionName: sessionName,
          pendingSessionName: this.pendingSessionScreenshot.sessionName,
        },
      });
      throw new Error('A session screenshot request is already in flight');
    }

    const message = generators.screenshotSession.captureScreenshot('host', sessionName, width, height);

    const sendTs = Date.now();
    const promise = new Promise<SessionScreenshotResult>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingSessionScreenshot = null;
        Sentry.logger.info('[PlayjectorService] macro-recorder captureSessionScreenshot timed out', {
          feature: 'macro-recorder', sessionName, timeoutMs: SESSION_SCREENSHOT_TIMEOUT_MS,
        });
        Sentry.captureMessage('[MACRO-SCREENSHOT] captureSessionScreenshot timed out — no Playjector reply', {
          level: 'error',
          extra: { sessionName, timeoutMs: SESSION_SCREENSHOT_TIMEOUT_MS },
        });
        reject(new Error(`captureSessionScreenshot timed out after ${SESSION_SCREENSHOT_TIMEOUT_MS}ms`));
      }, SESSION_SCREENSHOT_TIMEOUT_MS);
      this.pendingSessionScreenshot = { resolve, reject, timeoutId, sessionName, sentAt: sendTs };
    });

    Sentry.logger.info('[PlayjectorService] macro-recorder IPC send captureSessionScreenshot', {
      feature: 'macro-recorder', transport: 'ipc', direction: 'send',
      action: 'captureSessionScreenshot', sessionName, width, height,
    });
    console.log('[PlayjectorService] 📤 Sending captureSessionScreenshot IPC', { sessionName, width, height });
    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'sending captureSessionScreenshot IPC',
      data: { sessionName, width, height },
    });
    ipcSend(message);

    return promise;
  }

  public notifySessionScreenshotResult(reply: {
    result: string;
    sessionName: string;
    index: number;
    filePath: string;
    timestampMs: number;
    error?: string;
  }): void {
    const waiter = this.pendingSessionScreenshot;
    if (!waiter) {
      console.warn('[PlayjectorService] sessionScreenshot reply with no waiter — dropping');
      Sentry.captureMessage('[MACRO-SCREENSHOT] sessionScreenshot reply dropped — no waiter', {
        level: 'warning',
        extra: { sessionName: reply.sessionName, result: reply.result, error: reply.error },
      });
      return;
    }
    if (waiter.sessionName !== reply.sessionName) {
      console.warn('[PlayjectorService] sessionScreenshot sessionName mismatch — dropping');
      Sentry.captureMessage('[MACRO-SCREENSHOT] sessionScreenshot reply dropped — sessionName mismatch', {
        level: 'warning',
        extra: { expected: waiter.sessionName, received: reply.sessionName },
      });
      return;
    }
    this.pendingSessionScreenshot = null;
    clearTimeout(waiter.timeoutId);

    const latencyMs = Date.now() - waiter.sentAt;

    if (reply.error) {
      Sentry.logger.info('[PlayjectorService] macro-recorder notifySessionScreenshotResult error', {
        feature: 'macro-recorder', sessionName: reply.sessionName, error: reply.error, latencyMs,
      });
      Sentry.addBreadcrumb({
        category: 'MACRO-SCREENSHOT',
        level: 'warning',
        message: 'sessionScreenshot error reply',
        data: { sessionName: reply.sessionName, error: reply.error },
      });
      waiter.reject(new Error(`Session screenshot error: ${reply.error}`));
      return;
    }
    Sentry.logger.info('[PlayjectorService] macro-recorder notifySessionScreenshotResult resolved', {
      feature: 'macro-recorder', sessionName: reply.sessionName, index: reply.index, latencyMs,
    });
    Sentry.addBreadcrumb({
      category: 'MACRO-SCREENSHOT',
      level: 'info',
      message: 'sessionScreenshot resolved',
      data: {
        sessionName: reply.sessionName,
        index: reply.index,
        filePath: reply.filePath,
        timestampMs: reply.timestampMs,
      },
    });
    waiter.resolve({ index: reply.index, filePath: reply.filePath, timestampMs: reply.timestampMs });
  }
}

/**
 * Build a processMacroStepMSG IPC envelope.
 *
 * `processMacroStepMSG` does not yet have a Zod validator in `libs/Messaging`
 * (the wire schema authority is `apps/playjector/src/PlayJectorLib/source/
 * SchemaList.cpp::processMacroStepMSG`, consumed by
 * `ProcessDirectory::onMacroStep`), so we construct the envelope manually.
 * The TS shape, however, IS now in `libs/Messaging/src/types/messages/
 * process.ts` as `PlaycastMessageProcessMacroStep`, so the envelope is fully
 * typed end-to-end and host-electron's `messageHandler.ts` can narrow on
 * `header.action === 'macroStep'`.
 *
 * The optional `screenshotName` controls the filename Playjector uses when
 * saving the captured screenshot to disk (defaults to "macro_latest" in C++).
 */
function buildProcessMacroStepMessage(
  variants: PlaycastMessageProcessMacroStep['message']['variants'],
  screenshotName?: string,
): PlaycastMessage<PlaycastMessageProcessMacroStep> {
  return {
    signature: 'playcast',
    header: {
      tag: `${Date.now()}.macro-step`,
      source: 'host',
      schemaVersion: 5,
      timestamp: Date.now(),
      user: { id: 'host', auth: 'host' },
      target: 'process',
      action: 'macroStep',
      isReply: false,
    },
    body: {
      message: { variants, ...(screenshotName ? { screenshotName } : {}) },
      timings: [],
      stats: [],
      echo: false,
    },
  };
}
